export type DressCondition = "like_new" | "excellent" | "good" | "fair";
export type DressCategory = "wedding" | "engagement" | "party" | "casual" | "formal";
export type DressSize = "XS" | "S" | "M" | "L" | "XL" | "XXL";

export interface Message {
  id: string;
  senderId: string;
  text: string;
  timestamp: Date;
}

export interface Conversation {
  id: string;
  listingId: string;
  buyerId: string;
  sellerId: string;
  messages: Message[];
  lastMessage?: string;
  lastTimestamp?: Date;
}

export interface Listing {
  id: string;
  title: string;
  category: DressCategory;
  brand?: string;
  size: DressSize;
  condition: DressCondition;
  originalPrice: number;
  sellingPrice: number;
  eventWornAt?: string;
  description: string;
  images: string[];
  enhanced: boolean;
  sellerId: string;
  sellerName: string;
  sellerAvatar: string;
  location: string;
  distanceKm: number;
  wornCount: number;
  savedBy: string[];
  createdAt: Date;
  views: number;
}

export const SAMPLE_LISTINGS: Listing[] = [
  {
    id: "1",
    title: "Ivory Lace Midi Wedding Guest Dress",
    category: "wedding",
    brand: "Zara",
    size: "S",
    condition: "like_new",
    originalPrice: 1200,
    sellingPrice: 380,
    eventWornAt: "Sara's wedding, June 2024",
    description: "Wore this once to a beautiful outdoor wedding. Got so many compliments! Delicate lace overlay, fully lined. Dry cleaned after event.",
    images: [
      "https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=600&q=80",
      "https://images.unsplash.com/photo-1566479179817-0b9a5e76b5db?w=600&q=80",
    ],
    enhanced: true,
    sellerId: "u2",
    sellerName: "Nour Al-Hassan",
    sellerAvatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80",
    location: "Riyadh",
    distanceKm: 1.2,
    wornCount: 1,
    savedBy: ["u1"],
    createdAt: new Date("2024-11-10"),
    views: 142,
  },
  {
    id: "2",
    title: "Blush Satin Maxi – Engagement Party",
    category: "engagement",
    brand: "H&M",
    size: "M",
    condition: "excellent",
    originalPrice: 850,
    sellingPrice: 250,
    eventWornAt: "My cousin's engagement, April 2024",
    description: "Stunning blush satin gown with thigh-high slit. One wear only. Perfect for engagements or formal events.",
    images: [
      "https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=600&q=80",
      "https://images.unsplash.com/photo-1612336307429-8a898d10e223?w=600&q=80",
    ],
    enhanced: true,
    sellerId: "u3",
    sellerName: "Lina Khoury",
    sellerAvatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80",
    location: "Riyadh",
    distanceKm: 2.8,
    wornCount: 1,
    savedBy: [],
    createdAt: new Date("2024-11-08"),
    views: 89,
  },
  {
    id: "3",
    title: "Emerald Green Mini Party Dress",
    category: "party",
    brand: "Mango",
    size: "XS",
    condition: "good",
    originalPrice: 600,
    sellingPrice: 150,
    eventWornAt: "New Year's Eve party",
    description: "Eye-catching emerald mini with ruched details. Worn twice but still looks incredible. Perfect for club nights or birthday parties.",
    images: [
      "https://images.unsplash.com/photo-1539008835657-9e8e9680c956?w=600&q=80",
    ],
    enhanced: false,
    sellerId: "u4",
    sellerName: "Reem Fadel",
    sellerAvatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&q=80",
    location: "Jeddah",
    distanceKm: 3.5,
    wornCount: 2,
    savedBy: ["u1"],
    createdAt: new Date("2024-11-05"),
    views: 67,
  },
  {
    id: "4",
    title: "Navy Blue Floral Wrap Dress – Formal",
    category: "formal",
    brand: "Massimo Dutti",
    size: "L",
    condition: "excellent",
    originalPrice: 1100,
    sellingPrice: 320,
    description: "Classic navy wrap dress with delicate floral print. Timeless and elegant. Worn once to a formal dinner.",
    images: [
      "https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=600&q=80",
      "https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=600&q=80",
    ],
    enhanced: true,
    sellerId: "u5",
    sellerName: "Dina Mahmoud",
    sellerAvatar: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=100&q=80",
    location: "Riyadh",
    distanceKm: 0.9,
    wornCount: 1,
    savedBy: [],
    createdAt: new Date("2024-11-12"),
    views: 201,
  },
  {
    id: "5",
    title: "Rose Gold Sequin Party Dress",
    category: "party",
    brand: "Zara",
    size: "S",
    condition: "like_new",
    originalPrice: 950,
    sellingPrice: 280,
    eventWornAt: "Friend's bachelorette party",
    description: "Absolutely stunning rose gold sequin mini dress. All eyes will be on you. Only worn for 3 hours! Still has tags.",
    images: [
      "https://images.unsplash.com/photo-1572804013309-59a88b7e92f1?w=600&q=80",
    ],
    enhanced: true,
    sellerId: "u6",
    sellerName: "Yasmine Haddad",
    sellerAvatar: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&q=80",
    location: "Riyadh",
    distanceKm: 4.1,
    wornCount: 1,
    savedBy: ["u1"],
    createdAt: new Date("2024-11-14"),
    views: 315,
  },
  {
    id: "6",
    title: "White Cotton Sundress – Casual Chic",
    category: "casual",
    brand: "& Other Stories",
    size: "M",
    condition: "good",
    originalPrice: 480,
    sellingPrice: 110,
    description: "Breezy white cotton sundress perfect for brunches or casual outings. Worn a handful of times, laundered with care.",
    images: [
      "https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?w=600&q=80",
    ],
    enhanced: false,
    sellerId: "u7",
    sellerName: "Mariam Aziz",
    sellerAvatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&q=80",
    location: "Riyadh",
    distanceKm: 5.2,
    wornCount: 4,
    savedBy: [],
    createdAt: new Date("2024-11-01"),
    views: 43,
  },
];

let listings: Listing[] = [...SAMPLE_LISTINGS];
let conversations: Conversation[] = [
  {
    id: "c1",
    listingId: "1",
    buyerId: "u1",
    sellerId: "u2",
    messages: [
      { id: "m1", senderId: "u1", text: "Is this still available?", timestamp: new Date("2024-11-15T10:00:00") },
      { id: "m2", senderId: "u2", text: "Yes it is! Would you like to see more photos?", timestamp: new Date("2024-11-15T10:05:00") },
      { id: "m3", senderId: "u1", text: "Can you lower the price a bit?", timestamp: new Date("2024-11-15T10:10:00") },
    ],
    lastMessage: "Can you lower the price a bit?",
    lastTimestamp: new Date("2024-11-15T10:10:00"),
  }
];

// Simulate async API
export const getListings = async (): Promise<Listing[]> => {
  await new Promise(r => setTimeout(r, 300));
  return listings;
};

export const getListing = async (id: string): Promise<Listing | undefined> => {
  await new Promise(r => setTimeout(r, 150));
  return listings.find(l => l.id === id);
};

export const createListing = async (data: Omit<Listing, "id" | "createdAt" | "views" | "savedBy" | "wornCount" | "enhanced">): Promise<Listing> => {
  await new Promise(r => setTimeout(r, 800));
  const newListing: Listing = {
    ...data,
    id: Date.now().toString(),
    createdAt: new Date(),
    views: 0,
    savedBy: [],
    wornCount: 1,
    enhanced: true,
  };
  listings = [newListing, ...listings];
  return newListing;
};

export const toggleSave = async (listingId: string, userId: string): Promise<boolean> => {
  await new Promise(r => setTimeout(r, 100));
  const listing = listings.find(l => l.id === listingId);
  if (!listing) return false;
  const idx = listing.savedBy.indexOf(userId);
  if (idx > -1) {
    listing.savedBy.splice(idx, 1);
    return false;
  } else {
    listing.savedBy.push(userId);
    return true;
  }
};

export const getConversations = async (userId: string): Promise<Conversation[]> => {
  await new Promise(r => setTimeout(r, 200));
  return conversations.filter(c => c.buyerId === userId || c.sellerId === userId);
};

export const sendMessage = async (convId: string, senderId: string, text: string): Promise<Message> => {
  await new Promise(r => setTimeout(r, 150));
  const msg: Message = { id: Date.now().toString(), senderId, text, timestamp: new Date() };
  const conv = conversations.find(c => c.id === convId);
  if (conv) {
    conv.messages.push(msg);
    conv.lastMessage = text;
    conv.lastTimestamp = new Date();
  }
  return msg;
};

export const startConversation = async (listingId: string, sellerId: string, buyerId: string, initialMessage: string): Promise<Conversation> => {
  await new Promise(r => setTimeout(r, 200));
  const existing = conversations.find(c => c.listingId === listingId && c.buyerId === buyerId);
  if (existing) return existing;
  const conv: Conversation = {
    id: Date.now().toString(),
    listingId,
    buyerId,
    sellerId,
    messages: [{ id: Date.now().toString(), senderId: buyerId, text: initialMessage, timestamp: new Date() }],
    lastMessage: initialMessage,
    lastTimestamp: new Date(),
  };
  conversations.push(conv);
  return conv;
};

export const CURRENT_USER = {
  id: "u1",
  name: "Hana Al-Rashid",
  avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&q=80",
  location: "Riyadh",
};
